/**
 * Shared building blocks for the Blinkit-theme customer screens (rendered only for tenants whose theme preset has
 * ``homeLayout: "blinkit"`` — see lib/storefrontTheme.js). One header, one card, one empty/error/skeleton look, so
 * search, categories, product, cart, checkout, orders and account read as a single app.
 *
 * Visual language (matches BlinkitHome): cool off-white page, white cards with a hairline border and 16px radius,
 * extra-bold 15–19px headings, the theme's primary blue for actions, near-black for strong accents.
 */
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";

/** Sticky white page header: back button, title / subtitle, optional right-hand actions. */
export function BkPageHeader({ title, subtitle, right, back, onBack, children, testid = "bk-page-header" }) {
    const nav = useNavigate();
    const goBack = () => {
        if (onBack) return onBack();
        if (back) return nav(back);
        // A deep link has no in-app history to return to — fall back to home rather than leaving the app.
        if (window.history.length > 1) return nav(-1);
        return nav("/");
    };
    return (
        <header className="sticky top-0 z-30 bg-card border-b border-border" data-testid={testid}>
            <div className="h-14 px-2 flex items-center gap-1">
                <button type="button" onClick={goBack} aria-label="Go back" data-testid="bk-back"
                        className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center hover:bg-muted active:scale-95 transition">
                    <ArrowLeft size={21} />
                </button>
                <div className="min-w-0 flex-1 pr-1">
                    {title != null && <h1 className="text-[17px] font-extrabold leading-tight truncate">{title}</h1>}
                    {subtitle && <p className="text-[12px] text-muted-foreground leading-tight truncate mt-0.5">{subtitle}</p>}
                </div>
                {right && <div className="flex items-center gap-1 shrink-0 pr-1">{right}</div>}
            </div>
            {children}
        </header>
    );
}

/** A white section card. ``title`` renders the standard section heading; ``action`` sits on its right. */
export function BkCard({ title, action, icon: Icon, children, className = "", bodyClassName = "", testid }) {
    return (
        <section className={`bg-card rounded-2xl border border-border/80 ${className}`} data-testid={testid}>
            {title && (
                <div className="flex items-center justify-between gap-3 px-4 pt-4">
                    <h2 className="text-[15px] font-extrabold leading-tight inline-flex items-center gap-2 min-w-0">
                        {Icon && <Icon size={17} className="text-primary shrink-0" />}
                        <span className="truncate">{title}</span>
                    </h2>
                    {action}
                </div>
            )}
            <div className={title ? `px-4 pb-4 pt-3 ${bodyClassName}` : `p-4 ${bodyClassName}`}>{children}</div>
        </section>
    );
}

export function BkSectionTitle({ children, action, className = "" }) {
    return (
        <div className={`flex items-end justify-between gap-3 ${className}`}>
            <h2 className="text-[18px] font-extrabold tracking-tight leading-tight">{children}</h2>
            {action}
        </div>
    );
}

/** Empty / not-found state: icon in a soft circle, a title, one line of help, one action. */
export function BkEmptyState({ icon: Icon, title, body, action, testid = "bk-empty", className = "" }) {
    return (
        <div className={`flex flex-col items-center text-center px-6 py-12 ${className}`} data-testid={testid}>
            {Icon && (
                <span className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon size={28} />
                </span>
            )}
            <p className="text-[16px] font-extrabold">{title}</p>
            {body && <p className="text-[13px] text-muted-foreground mt-1.5 max-w-[34ch] leading-relaxed">{body}</p>}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

/** Load failure with a retry. */
export function BkErrorState({ title = "Couldn't load this page", body = "Check your connection and try again.", onRetry, testid = "bk-error" }) {
    return (
        <BkEmptyState
            icon={AlertTriangle}
            title={title}
            body={body}
            testid={testid}
            action={onRetry && (
                <button type="button" onClick={onRetry} className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold active:scale-95 transition">
                    Try again
                </button>
            )}
        />
    );
}

export function BkSpinner({ label = "Loading…" }) {
    return (
        <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-muted-foreground" role="status">
            <Loader2 size={16} className="animate-spin" /> {label}
        </div>
    );
}

/** Placeholder grid in the exact footprint of product tiles, so the page doesn't jump when data lands. */
export function BkTileSkeletonGrid({ count = 6, className = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4", testid = "bk-skeleton" }) {
    return (
        <div className={`grid gap-3 ${className}`} data-testid={testid} aria-hidden="true">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="bg-card rounded-2xl border border-border/80 p-2">
                    <div className="aspect-square rounded-xl bg-muted animate-pulse" />
                    <div className="mt-2.5 h-3 rounded bg-muted animate-pulse w-11/12" />
                    <div className="mt-1.5 h-3 rounded bg-muted animate-pulse w-2/3" />
                    <div className="mt-3 flex items-center justify-between">
                        <div className="h-4 w-10 rounded bg-muted animate-pulse" />
                        <div className="h-8 w-16 rounded-lg bg-muted animate-pulse" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Rounded filter chip (active = primary tint). */
export function BkChip({ active, onClick, children, testid, className = "" }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid} aria-pressed={!!active}
                className={`h-9 shrink-0 px-3.5 rounded-full border text-[13px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 transition active:scale-95 ${active ? "bg-primary/10 border-primary text-primary" : "bg-card border-border text-foreground hover:border-foreground/30"} ${className}`}>
            {children}
        </button>
    );
}
